import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { playSound } from '../lib/soundEffects';

const { FiCopy, FiTrash2, FiGitMerge, FiX, FiCheck, FiArrowRight } = FiIcons;

export default function DuplicateResolverModal({
  isOpen,
  onClose,
  pair, // { original, duplicate }
  onResolveMerge,
  onDeleteDuplicate,
}) {
  if (!isOpen || !pair) return null;
  const { original, duplicate } = pair;

  const handleMerge = () => {
    playSound('launch');
    // Merge notes & tags
    const mergedNotes = [original.note, duplicate.note].filter(Boolean).join('\n---\n');
    const mergedClicks = (original.clicks || 0) + (duplicate.clicks || 0);
    onResolveMerge?.(original.id, duplicate.id, {
      note: mergedNotes,
      clicks: mergedClicks,
    });
    onClose();
  };

  const handleDelete = () => {
    playSound('lock');
    onDeleteDuplicate?.(duplicate.id);
    onClose();
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black/80 backdrop-blur-md"
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 15 }}
          className="relative w-full max-w-xl rounded-2xl bg-[#121216] border border-[#26262e] shadow-2xl p-6 z-10 flex flex-col font-sans"
        >
          {/* Header */}
          <div className="flex items-center justify-between pb-3 border-b border-[#24242c]">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400">
                <SafeIcon icon={FiGitMerge} className="w-4 h-4" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-white">Duplicate Resolution Wizard</h3>
                <p className="text-[10px] text-[#777] font-mono">
                  Side-by-side comparison & intelligent merge
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1 text-[#888] hover:text-white rounded-lg transition-colors"
            >
              <SafeIcon icon={FiX} className="w-4 h-4" />
            </button>
          </div>

          {/* Side by side comparison cards */}
          <div className="grid grid-cols-2 gap-3 my-4">
            {/* Original Card */}
            <div className="p-3.5 rounded-xl bg-[#16161f] border border-emerald-500/30 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-mono uppercase text-emerald-400 font-bold px-1.5 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/20">
                    Primary / Original
                  </span>
                  <span className="text-[10px] text-[#777] font-mono">⚡ {original.clicks || 0} hits</span>
                </div>
                <h4 className="text-xs font-semibold text-white line-clamp-1">{original.title}</h4>
                <p className="text-[10px] text-[#777] font-mono truncate mt-0.5">{original.url}</p>
                <div className="mt-2 text-[11px] text-[#aaa] bg-black/30 p-2 rounded-lg font-mono min-h-[44px] line-clamp-2">
                  {original.note || <span className="text-[#555] italic">No note</span>}
                </div>
              </div>
              <span className="text-[10px] text-[#666] font-mono mt-3">Folder: {original.folderTitle || 'Home'}</span>
            </div>

            {/* Duplicate Card */}
            <div className="p-3.5 rounded-xl bg-[#16161f] border border-amber-500/30 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-mono uppercase text-amber-400 font-bold px-1.5 py-0.5 rounded bg-amber-500/10 border border-amber-500/20">
                    Duplicate Copy
                  </span>
                  <span className="text-[10px] text-[#777] font-mono">⚡ {duplicate.clicks || 0} hits</span>
                </div>
                <h4 className="text-xs font-semibold text-white line-clamp-1">{duplicate.title}</h4>
                <p className="text-[10px] text-[#777] font-mono truncate mt-0.5">{duplicate.url}</p>
                <div className="mt-2 text-[11px] text-[#aaa] bg-black/30 p-2 rounded-lg font-mono min-h-[44px] line-clamp-2">
                  {duplicate.note || <span className="text-[#555] italic">No note</span>}
                </div>
              </div>
              <span className="text-[10px] text-[#666] font-mono mt-3">Folder: {duplicate.folderTitle || 'Home'}</span>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#202028]">
            <button
              onClick={handleDelete}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold text-red-400 hover:bg-red-500/10 border border-red-500/30 transition-colors"
            >
              <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" />
              <span>Purge Duplicate Only</span>
            </button>
            <button
              onClick={handleMerge}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-black bg-emerald-400 hover:bg-emerald-300 transition-transform active:scale-95 shadow-md shadow-emerald-500/20"
            >
              <SafeIcon icon={FiGitMerge} className="w-3.5 h-3.5" />
              <span>Merge Data &amp; Consolidate</span>
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
